import { Injectable, Inject } from "@angular/core";
import { Router } from "@angular/router";
import { authSelectors } from "@content-platform/web-angular-shared-util-auth";
import { select, Store } from "@ngrx/store";
import { catchError, filter, map, of, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { userPermissionListActions } from "../actions";
import { APPLICATION_API_OPTIONS } from "../application-api-config";
import { userPermissionListSelectors } from "../selectors";
import { OKTA_AUTH_DEFAULT_ACCESS_APPS } from "../constants";
import * as i0 from "@angular/core";
import * as i1 from "@ngrx/store";
import * as i2 from "@angular/router";
export class UserPermissionListGuard {
    constructor(store, authStore, router, options) {
        this.store = store;
        this.authStore = authStore;
        this.router = router;
        this.appName = options.appName;
        this.oktaAuthDefaultAccess = OKTA_AUTH_DEFAULT_ACCESS_APPS.includes(this.appName);
    }
    canActivate() {
        console.log("UserPermissionListGuard canActivate");
        return this.checkLoggedIn().pipe(switchMap((loggedIn) => {
            console.log("UserPermissionListGuard is loggedIn", loggedIn);
            if (!loggedIn) {
                return of(false);
            }
            const permissionSub = this.checkStoreAndWaitForLoad().pipe(switchMap((loadedPermissions) => {
                if (this.oktaAuthDefaultAccess) {
                    return of(true);
                }
                if (loadedPermissions) {
                    return this.checkIfUserHasPermissions().pipe(tap((hasPermission) => {
                        if (!hasPermission) {
                            this.router.navigate(["/access-denied"], { skipLocationChange: true });
                        }
                    }));
                }
                console.log("UserPermissionListGuard loadedPermissions", loadedPermissions);
                return of(loadedPermissions);
            }), catchError(() => (this.oktaAuthDefaultAccess ? of(true) : of(false))));
            if (this.oktaAuthDefaultAccess) {
                permissionSub.pipe(take(1)).subscribe();
                return of(true);
            }
            else {
                return permissionSub;
            }
        }));
    }
    checkLoggedIn() {
        return this.store.pipe(select(authSelectors.getADUser)).pipe(map((user) => !!user));
    }
    /**
     * Checks to see if a user permission list has been loaded
     * Side effect: if it hasn't, wait for it to load if possible
     *
     * @returns {Observable<boolean>}
     * @memberof UserPermissionListGuard
     */
    checkStoreAndWaitForLoad() {
        let triedToLoadPermissionList = false;
        return this.store.pipe(select(userPermissionListSelectors.getUserPermissionListLoaded)).pipe(withLatestFrom(this.store.pipe(select(userPermissionListSelectors.getUserPermissionListLoading)), this.store.pipe(select(authSelectors.getADUser)), this.store.pipe(select(authSelectors.getIsSettingUser)), this.store.pipe(select(authSelectors.getLoginFailed))), filter(([loaded, loading, user, settingUser, loginFailed]) => {
            // If login failed, continue
            if (loginFailed) {
                return true;
            }
            // If the permission list hasn't loaded yet, nothing is currently loading, we haven't tried to load the list yet
            // but we have a valid user, try loading the permission list (this should set loading to true on subsequent runs)
            // Wait for some state change before continuing
            if (!loaded && !loading && !triedToLoadPermissionList && user) {
                this.store.dispatch(new userPermissionListActions.LoadById(user.profile.oid));
                triedToLoadPermissionList = true;
                return false;
            }
            // If we aren't loading anything, and we're also not trying to set the user continue
            // In this case, either:
            // the list is already loaded (good),
            // or we haven't tried to load the list and we'll never have a valid user in order to do so
            return !loading && !settingUser;
        }), map(([loaded]) => loaded), take(1));
    }
    checkIfUserHasPermissions() {
        return this.store.pipe(select(userPermissionListSelectors.hasUserPermission));
    }
    static { this.ɵfac = function UserPermissionListGuard_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || UserPermissionListGuard)(i0.ɵɵinject(i1.Store), i0.ɵɵinject(i1.Store), i0.ɵɵinject(i2.Router), i0.ɵɵinject(APPLICATION_API_OPTIONS)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: UserPermissionListGuard, factory: UserPermissionListGuard.ɵfac }); }
}
//# sourceMappingURL=user-permission-list.guard.js.map